import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const ESTADO_CONFIG = {
  PENDIENTE:   { icon: '○', label: 'Pendiente',   cls: 'text-white/50' },
  RECIBIDO:    { icon: '📦', label: 'Recibido',    cls: 'text-blue-400' },
  EN_PROCESO:  { icon: '▶', label: 'En proceso',  cls: 'text-yellow-400' },
  COMPLETADO:  { icon: '✓', label: 'Completado',  cls: 'text-green-400' },
  INCIDENCIA:  { icon: '!', label: 'Incidencia',  cls: 'text-red-400' },
  CANCELADO:   { icon: '✕', label: 'Cancelado',   cls: 'text-white/30' },
};

const PIEZA_ESTADO_CONFIG = {
  PENDIENTE:   { icon: '○', cls: 'text-white/40' },
  EN_PROCESO:  { icon: '▶', cls: 'text-yellow-400' },
  COMPLETADA:  { icon: '✓', cls: 'text-green-400' },
  INCIDENCIA:  { icon: '!', cls: 'text-red-400' },
};

// Days available for "Mover a..." menu
const DIAS_SEMANA = [
  { label: 'Lunes',      dow: 1 },
  { label: 'Martes',     dow: 2 },
  { label: 'Miércoles',  dow: 3 },
  { label: 'Jueves',     dow: 4 },
  { label: 'Viernes',    dow: 5 },
  { label: 'Sábado',     dow: 6 },
];

const TrabajoCard = ({
  trabajo,
  isOpen = false,
  onToggle,
  onEstadoChange,
  onMover,
  onEditar,
  onEliminar,
  onAjustarPiezas,
  onEmpezarPieza,
  onVerRegistro,
  onEmpezar,
  onIncidencia,
  onMarcarRecibido,
  onMoverAFecha,      // NEW: (trabajoId, fechaDestino) => void
  isDragging = false,
  fromDate,
  onDragStart,
  onDragEnd,
  dragHandleProps = {}, // from dnd-kit useSortable
}) => {
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [showMoverMenu, setShowMoverMenu] = useState(false);
  const [confirmMove, setConfirmMove] = useState(null); // { label, fecha }

  const clienteNombre = trabajo?.cliente?.nombre || 'Sin cliente';
  const productoNombre = trabajo?.producto?.nombre || 'Sin producto';
  const empleadoNombre = trabajo?.empleado?.nombre || null;
  const color = trabajo?.clienteColor || '#6B7280';
  const estado = trabajo?.estado || 'PENDIENTE';
  const estadoCfg = ESTADO_CONFIG?.[estado] || ESTADO_CONFIG?.PENDIENTE;
  const piezas = trabajo?.piezas || [];
  const completadas = piezas?.filter(p => p?.estado === 'COMPLETADA')?.length;
  const total = trabajo?.cantidad_piezas || piezas?.length || 1;
  const recibidasDesdePiezas = piezas?.filter(p => p?.recibida)?.length;
  const recibidas = piezas?.length > 0 ? recibidasDesdePiezas : (trabajo?.cantidad_recibida || 0);
  const esUrgente = trabajo?.prioridad === 'URGENTE';
  const esRecurrente = trabajo?.tipo_trabajo === 'RECURRENTE';
  const tieneObs = !!(trabajo?.observaciones);
  const quiereHuesos = trabajo?.quiere_huesos;
  const guardarMerma = trabajo?.guardar_merma;
  const formatoCorte = trabajo?.formato_corte;
  const allCompleted = completadas === total && total > 0;
  const esCancelado = estado === 'CANCELADO';
  const tieneIncidencia = estado === 'INCIDENCIA';
  const esCompletado = estado === 'COMPLETADO';

  // ── Drag guard: COMPLETADO cannot be dragged ──────────────────────────────
  // dragHandleProps are spread onto the handle div; if COMPLETADO, we block them
  const effectiveDragHandleProps = esCompletado ? {} : dragHandleProps;

  // Minimal status badge for closed card
  const getClosedBadge = () => {
    if (allCompleted) return { text: '✓', cls: 'text-green-400' };
    if (tieneIncidencia) return { text: '!', cls: 'text-red-400' };
    if (estado === 'EN_PROCESO') return { text: '▶', cls: 'text-yellow-400' };
    if (total > 1) return { text: `${completadas}/${total}`, cls: 'text-white/40' };
    return null;
  };

  const closedBadge = getClosedBadge();

  // ── "Mover a..." date calculation ─────────────────────────────────────────
  // Given a day-of-week (1=Mon..6=Sat), find the nearest future date for that DOW
  // relative to the current week (fromDate)
  const getFechaForDow = (dow) => {
    const base = fromDate ? new Date(fromDate + 'T12:00:00') : new Date();
    // Find the Monday of the current week
    const baseDow = base?.getDay() === 0 ? 7 : base?.getDay(); // 1=Mon..7=Sun
    const diffToMon = 1 - baseDow;
    const monday = new Date(base);
    monday?.setDate(base?.getDate() + diffToMon);
    // Target date = monday + (dow - 1) days
    const target = new Date(monday);
    target?.setDate(monday?.getDate() + (dow - 1));
    const y = target?.getFullYear();
    const m = String(target?.getMonth() + 1)?.padStart(2, '0');
    const d = String(target?.getDate())?.padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleMoverA = (dia) => {
    const fecha = getFechaForDow(dia?.dow);
    if (estado === 'EN_PROCESO') {
      setConfirmMove({ label: dia?.label, fecha });
      setShowMoverMenu(false);
      return;
    }
    setShowMoverMenu(false);
    setShowMoreActions(false);
    onMoverAFecha?.(trabajo?.id, fecha);
  };

  const handleMoverASinProgramar = () => {
    if (estado === 'EN_PROCESO') {
      setConfirmMove({ label: 'Sin programar', fecha: null });
      setShowMoverMenu(false);
      return;
    }
    setShowMoverMenu(false);
    setShowMoreActions(false);
    onMoverAFecha?.(trabajo?.id, null);
  };

  const handleConfirmMoveConfirm = () => {
    onMoverAFecha?.(trabajo?.id, confirmMove?.fecha);
    setConfirmMove(null);
    setShowMoreActions(false);
  };

  return (
    <div
      className={`relative rounded-xl select-none transition-all duration-200 overflow-hidden ${
        isDragging ? 'opacity-40 scale-[0.97]' : ''
      } ${esCancelado ? 'opacity-60' : ''}`}
      style={{
        background: isOpen
          ? `linear-gradient(160deg, ${color}18 0%, ${color}08 60%, rgba(17,17,17,0.97) 100%)`
          : `rgba(17,17,17,0.85)`,
        border: `1px solid ${color}30`,
        borderLeft: `4px solid ${color}`,
      }}
    >
      {/* ── CONFIRMATION DIALOG for EN_PROCESO move ─────────────────────── */}
      {confirmMove && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/80 rounded-xl p-3 gap-2">
          <div className="text-white/80 text-xs font-oranienbaum text-center">
            Este trabajo está <span className="text-yellow-400 font-semibold">EN PROCESO</span>.<br />
            ¿Mover a <span className="text-white font-semibold">{confirmMove?.label}</span>?
          </div>
          <div className="flex gap-2 mt-1">
            <button
              onClick={() => setConfirmMove(null)}
              className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum bg-white/10 text-white/60 hover:bg-white/15 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmMoveConfirm}
              className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum bg-yellow-400/20 text-yellow-400 border border-yellow-400/30 hover:bg-yellow-400/30 transition-colors"
            >
              Mover
            </button>
          </div>
        </div>
      )}

      {/* ── CLOSED / HEADER ROW ─────────────────────────────────── */}
      <div className="flex items-center gap-1.5 px-2 py-2.5 hover:bg-white/4 transition-colors">
        {/* Drag handle — dnd-kit listeners attached here; disabled for COMPLETADO */}
        <div
          {...effectiveDragHandleProps}
          className={`flex-shrink-0 px-1 py-0.5 rounded touch-none ${
            esCompletado
              ? 'text-white/10 cursor-not-allowed' :'text-white/25 hover:text-white/60 cursor-grab active:cursor-grabbing'
          }`}
          title={esCompletado ? 'Trabajo completado — no se puede mover' : 'Arrastrar'}
          onClick={(e) => e?.stopPropagation()}
        >
          <Icon name="GripVertical" size={13} />
        </div>

        {/* Color dot */}
        <div
          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ background: color, boxShadow: `0 0 5px ${color}70` }}
        />

        {/* Client name — click to toggle */}
        <button
          className="flex-1 text-left font-semibold text-white text-sm font-oranienbaum truncate leading-tight cursor-pointer"
          onClick={onToggle}
          type="button"
        >
          {clienteNombre}
          {esUrgente && (
            <span className="ml-1.5 text-[9px] font-bold text-red-400 bg-red-500/15 px-1 py-0.5 rounded uppercase">URG</span>
          )}
        </button>

        {/* Minimal badge */}
        {closedBadge && (
          <span className={`text-[11px] font-oranienbaum flex-shrink-0 ${closedBadge?.cls}`}>
            {closedBadge?.text}
          </span>
        )}

        {/* Chevron */}
        <button
          onClick={onToggle}
          className="text-white/30 flex-shrink-0 transition-transform duration-200 p-0.5"
          type="button"
          aria-expanded={isOpen}
        >
          <span style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform 0.2s' }}>
            <Icon name="ChevronDown" size={14} />
          </span>
        </button>
      </div>

      {/* ── EXPANDED CONTENT ────────────────────────────────────── */}
      {isOpen && (
        <div
          className="border-t px-3 pb-3 pt-2.5 space-y-2"
          style={{ borderColor: `${color}20` }}
          onClick={(e) => e?.stopPropagation()}
        >
          {/* ── CLIENTE SECTION ── */}
          <div className="text-[10px] text-white/30 font-oranienbaum uppercase tracking-widest mb-1">Cliente</div>

          {/* Producto */}
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[10px] text-white/35 font-oranienbaum">Producto</div>
              <div className="text-white/80 text-xs font-oranienbaum">{productoNombre}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-white/35 font-oranienbaum">Cantidad</div>
              <div className="text-white/80 text-xs font-oranienbaum">{total} {total === 1 ? 'pieza' : 'piezas'}</div>
            </div>
          </div>

          {/* Tipo corte + formato */}
          {(trabajo?.tipo_corte || formatoCorte) && (
            <div className="flex items-start gap-4">
              {trabajo?.tipo_corte && (
                <div>
                  <div className="text-[10px] text-white/35 font-oranienbaum">Tipo de corte</div>
                  <div className="text-white/70 text-xs font-oranienbaum capitalize">{trabajo?.tipo_corte?.toLowerCase()}</div>
                </div>
              )}
              {formatoCorte && (
                <div>
                  <div className="text-[10px] text-white/35 font-oranienbaum">Formato</div>
                  <div className="text-white/70 text-xs font-oranienbaum">{formatoCorte?.replace('G', 'g')?.replace('KG', 'kg')}</div>
                </div>
              )}
            </div>
          )}

          {/* Empleado + prioridad */}
          <div className="flex items-start gap-4">
            {empleadoNombre && (
              <div>
                <div className="text-[10px] text-white/35 font-oranienbaum">Empleado</div>
                <div className="text-white/70 text-xs font-oranienbaum">👤 {empleadoNombre}</div>
              </div>
            )}
            {esUrgente && (
              <div>
                <div className="text-[10px] text-white/35 font-oranienbaum">Prioridad</div>
                <div className="text-red-400 text-xs font-oranienbaum font-semibold">URGENTE</div>
              </div>
            )}
            {esRecurrente && (
              <div>
                <div className="text-[10px] text-white/35 font-oranienbaum">Tipo</div>
                <div className="text-xs font-oranienbaum" style={{ color }}>↻ Recurrente</div>
              </div>
            )}
          </div>

          {/* Huesos / Merma */}
          {(quiereHuesos || guardarMerma) && (
            <div className="flex items-center gap-2 flex-wrap">
              {quiereHuesos && (
                <span className="text-[10px] text-amber-400/80 font-oranienbaum bg-amber-400/10 px-2 py-0.5 rounded border border-amber-400/20">🦴 Quiere huesos</span>
              )}
              {guardarMerma && (
                <span className="text-[10px] text-green-400/80 font-oranienbaum bg-green-400/10 px-2 py-0.5 rounded border border-green-400/20">♻ Guardar merma</span>
              )}
            </div>
          )}

          {/* Observaciones */}
          {tieneObs && (
            <div className="p-2 rounded-lg bg-white/5 border border-white/8 text-[11px] text-white/55 font-oranienbaum italic">
              📝 {trabajo?.observaciones}
            </div>
          )}

          {/* Recepción */}
          <div>
            <div className="text-[10px] text-white/35 font-oranienbaum mb-0.5">Recepción</div>
            <div className="text-xs font-oranienbaum text-white/60">
              {recibidas} / {total} recibidas
            </div>
          </div>

          {/* Progreso */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] text-white/35 font-oranienbaum">Progreso</div>
              {allCompleted ? (
                <span className="text-[11px] text-green-400 font-oranienbaum font-semibold">✓ COMPLETADO</span>
              ) : esCancelado ? (
                <span className="text-[11px] text-white/30 font-oranienbaum">✕ CANCELADO</span>
              ) : (
                <span className="text-[11px] text-white/50 font-oranienbaum">{completadas} / {total}</span>
              )}
            </div>
            {total > 0 && !esCancelado && (
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${(completadas / total) * 100}%`, background: allCompleted ? '#34D399' : color }}
                />
              </div>
            )}
          </div>

          {/* ── PIEZAS INDIVIDUALES ── */}
          {piezas?.length > 0 && !esCancelado && (
            <div className="pt-1">
              <div className="text-[10px] text-white/30 font-oranienbaum uppercase tracking-widest mb-1.5">Piezas</div>
              <div className="space-y-1.5">
                {piezas?.map((pieza) => {
                  const pEstado = PIEZA_ESTADO_CONFIG?.[pieza?.estado] || PIEZA_ESTADO_CONFIG?.PENDIENTE;
                  const isPendiente = pieza?.estado === 'PENDIENTE';
                  const isEnProceso = pieza?.estado === 'EN_PROCESO';
                  const isCompletada = pieza?.estado === 'COMPLETADA';
                  const piezaEmpleado = pieza?.empleado?.nombre || null;
                  return (
                    <div key={pieza?.id} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg bg-white/4 border border-white/6">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`text-xs flex-shrink-0 ${pEstado?.cls}`}>{pEstado?.icon}</span>
                        <div className="min-w-0">
                          <span className="text-[11px] text-white/65 font-oranienbaum">
                            {productoNombre} {pieza?.numero_pieza}
                          </span>
                          {piezaEmpleado && (
                            <div className="text-[10px] text-white/35 font-oranienbaum">👤 {piezaEmpleado}</div>
                          )}
                          {pieza?.recibida && (
                            <div className="text-[10px] text-blue-400/70 font-oranienbaum">📦 Recibida</div>
                          )}
                        </div>
                      </div>
                      {(isPendiente || isEnProceso) && (
                        <button
                          onClick={(e) => { e?.stopPropagation(); onEmpezarPieza?.(trabajo, pieza); }}
                          className="text-[11px] px-3 py-1 rounded-lg font-oranienbaum font-semibold border transition-colors flex-shrink-0 active:scale-95"
                          style={{ background: `${color}25`, color, borderColor: `${color}50` }}
                        >
                          {isEnProceso ? '▶ Continuar' : 'EMPEZAR'}
                        </button>
                      )}
                      {isCompletada && (
                        <button
                          onClick={(e) => { e?.stopPropagation(); onVerRegistro?.(pieza); }}
                          className="text-[11px] px-2 py-1 rounded-lg font-oranienbaum border transition-colors flex-shrink-0 active:scale-95 text-green-400 border-green-400/30 bg-green-400/8 hover:bg-green-400/15"
                        >
                          👁 Ver Registro
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── ACCIONES ── */}
          {!esCancelado && (
            <div className="pt-2 border-t space-y-2" style={{ borderColor: `${color}15` }}>
              {estado === 'PENDIENTE' && (
                <button
                  onClick={() => onMarcarRecibido?.(trabajo)}
                  className="w-full py-2 rounded-lg text-sm font-oranienbaum font-semibold border transition-colors active:scale-95"
                  style={{ background: `${color}20`, color, borderColor: `${color}40` }}
                >
                  📦 Marcar como recibido
                </button>
              )}

              {/* More actions toggle */}
              <div>
                <button
                  onClick={() => setShowMoreActions(v => !v)}
                  className="flex items-center gap-1.5 text-[11px] text-white/40 hover:text-white/60 font-oranienbaum transition-colors"
                >
                  <Icon name="MoreHorizontal" size={13} />
                  <span>Más acciones</span>
                  <span className="transition-transform duration-150" style={{ display: 'inline-block', transform: showMoreActions ? 'rotate(180deg)' : 'none' }}>
                    <Icon name="ChevronDown" size={11} />
                  </span>
                </button>

                {showMoreActions && (
                  <div className="mt-2 space-y-1.5">
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        onClick={() => { onEditar?.(trabajo); setShowMoreActions(false); }}
                        className="py-2 rounded-lg text-xs font-oranienbaum bg-white/8 text-white/65 hover:bg-white/12 transition-colors border border-white/8 active:scale-95"
                      >
                        ✏️ Editar
                      </button>
                      <button
                        onClick={() => { onMover?.(trabajo); setShowMoreActions(false); }}
                        className="py-2 rounded-lg text-xs font-oranienbaum bg-white/8 text-white/65 hover:bg-white/12 transition-colors border border-white/8 active:scale-95"
                      >
                        📅 Mover (semana)
                      </button>
                      <button
                        onClick={() => { onEstadoChange?.(trabajo?.id, 'URGENTE'); setShowMoreActions(false); }}
                        className="py-2 rounded-lg text-xs font-oranienbaum bg-red-500/12 text-red-400 hover:bg-red-500/20 transition-colors border border-red-500/20 active:scale-95"
                      >
                        🔴 Urgente
                      </button>
                      <button
                        onClick={() => { onIncidencia?.(trabajo); setShowMoreActions(false); }}
                        className="py-2 rounded-lg text-xs font-oranienbaum bg-orange-500/12 text-orange-400 hover:bg-orange-500/20 transition-colors border border-orange-500/20 active:scale-95"
                      >
                        ⚠️ Incidencia
                      </button>
                    </div>

                    {/* ── MOVER A... ── */}
                    {!esCompletado && (
                      <div className="relative">
                        <button
                          onClick={() => setShowMoverMenu(v => !v)}
                          className="w-full py-2 rounded-lg text-xs font-oranienbaum bg-[#D6EF4E]/10 text-[#D6EF4E]/80 hover:bg-[#D6EF4E]/18 transition-colors border border-[#D6EF4E]/20 active:scale-95 flex items-center justify-center gap-1.5"
                        >
                          <Icon name="CalendarDays" size={12} />
                          Mover a...
                          <span style={{ display: 'inline-block', transform: showMoverMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                            <Icon name="ChevronDown" size={11} />
                          </span>
                        </button>

                        {showMoverMenu && (
                          <div className="mt-1 rounded-xl border border-white/10 bg-[#111111] overflow-hidden shadow-xl">
                            {DIAS_SEMANA?.map((dia) => {
                              const fecha = getFechaForDow(dia?.dow);
                              const isCurrentDay = fecha === fromDate;
                              return (
                                <button
                                  key={dia?.dow}
                                  onClick={() => handleMoverA(dia)}
                                  disabled={isCurrentDay}
                                  className={`w-full text-left px-3 py-2 text-xs font-oranienbaum transition-colors flex items-center justify-between ${
                                    isCurrentDay
                                      ? 'text-white/20 cursor-not-allowed' :'text-white/65 hover:bg-white/8 hover:text-white/90'
                                  }`}
                                >
                                  <span>{dia?.label}</span>
                                  {isCurrentDay && <span className="text-[10px] text-white/25">actual</span>}
                                </button>
                              );
                            })}
                            <div className="border-t border-white/8">
                              <button
                                onClick={handleMoverASinProgramar}
                                className="w-full text-left px-3 py-2 text-xs font-oranienbaum text-white/40 hover:bg-white/8 hover:text-white/70 transition-colors flex items-center gap-1.5"
                              >
                                <Icon name="CalendarX" size={11} />
                                Sin programar
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => { onEliminar?.(trabajo); setShowMoreActions(false); }}
                      className="w-full py-2 rounded-lg text-xs font-oranienbaum bg-red-500/8 text-red-400/70 hover:bg-red-500/15 transition-colors border border-red-500/12 active:scale-95"
                    >
                      🗑 Eliminar / Cancelar
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TrabajoCard;
